// eslint-disable-next-line jira/import-whitelist
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import type { ResourceStoreData } from '@atlassian/react-resource-router';
import { getDefaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { RESOURCE_TYPE_WORKSPACE_CONTEXT } from './emptyLogoResponse.constants.tsx';
import type {
	AdminSettingsResponse,
	AdminSettingsMap,
	BannerSettings,
	BannerSettingsResponse,
} from './BannerSettings.types.tsx';

export const SSR_SERVICE_NAME = 'jsm-cmdb-gateway';

const HEADER_X_SSR_SERVICE_NAME = 'x-ssr-service-name';
const HEADER_ATL_WORKSPACEID = 'atl-workspaceid';
const HEADER_X_SSR_ASAP_AUDIENCE = 'x-ssr-asap-audience';
const HEADER_LINKED_ASSETS_WORKSPACE_ID = 'linked-assets-workspace-id';

// Single reference to server variable to minimize ratcheting count
const isServer = __SERVER__;

export function extractResourceStatePromise(
	resourceState: ResourceStoreData,
): Promise<unknown> | null {
	return resourceState?.[RESOURCE_TYPE_WORKSPACE_CONTEXT]?.state?.promise;
}

export const mergeOptionsWithDefaults = (
	url: string,
	options: RequestInit | undefined,
): RequestInit => {
	const defaultOptions = getDefaultOptions(url);

	return {
		...defaultOptions,
		...options,
		headers: {
			...defaultOptions.headers,
			...options?.headers,
		},
	};
};

/**
 * Returns the appropriate admin settings URL based on execution context.
 *
 * @param workspaceId - The workspace identifier
 * @returns Server-side URL with /assets prefix when FG is ON, legacy URL when FG is OFF,
 *          or client-side gateway URL when running in browser
 */
export const getAdminSettingsUrl = (workspaceId: string): string => {
	if (isServer) {
		return '/assets/api/v1/admin/settings';
	}
	return `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/admin/settings`;
};

export const getBannerSettingsUrl = (workspaceId: string): string => {
	if (isServer) {
		return '/assets/api/v1/admin/banner-settings';
	}
	return `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/admin/banner-settings`;
};

export const getUserSettingsUrl = (workspaceId: string): string => {
	if (isServer) {
		return '/assets/api/v1/user/settings';
	}
	return `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/user/settings`;
};

export const getLogoUrl = (workspaceId: string): string => {
	if (isServer) {
		return '/assets/api/v1/assets-logo';
	}
	return `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/assets-logo`;
};

export const getFaviconUrl = (workspaceId: string): string => {
	if (isServer) {
		return '/assets/api/v1/admin/favicon';
	}
	return `/gateway/api/jsm/assets/workspace/${workspaceId}/v1/admin/favicon`;
};

export const getAssetsAdminHeaders = (workspaceId: string): Record<string, string> => {
	if (isServer) {
		return {
			[HEADER_X_SSR_ASAP_AUDIENCE]: 'assets-tempeh-archetype',
			...(fg('assets_tempeh_units_admin_primary_routing')
				? { [HEADER_LINKED_ASSETS_WORKSPACE_ID]: workspaceId }
				: {}),
		};
	}
	return {
		[HEADER_X_SSR_SERVICE_NAME]: SSR_SERVICE_NAME,
		[HEADER_ATL_WORKSPACEID]: workspaceId,
	};
};

export const transformAdminSettingsResponse = (
	response: AdminSettingsResponse,
): AdminSettingsMap => {
	return response.reduce((acc: AdminSettingsMap, setting) => {
		acc[setting.key] = setting;
		return acc;
	}, {});
};

/**
 * Transforms banner settings by ensuring content is properly parsed.
 * Handles both string (JSON) and object content formats.
 *
 * @param bannerSettings - The banner settings response or settings object
 * @returns Transformed banner settings with parsed content
 */
export const transformBannerSettings = (
	bannerSettings: BannerSettingsResponse | BannerSettings,
):
	| {
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			content: any;
			id: string;
			hashId?: string;
			isDismissable: boolean;
			isEnabled: boolean;
			visibility: string;
			bgColor: string;
	  }
	| {
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			content: any;
			id: string;
			hashId?: string;
			isDismissable: boolean;
			isEnabled: boolean;
			visibility: string;
			bgColor: string;
	  } => {
	const { content, ...rest } = bannerSettings;
	const isContentString = content && typeof content === 'string';

	return {
		...rest,
		content: isContentString ? JSON.parse(content) : JSON.stringify(content),
	};
};

export const getAssetsTenantContextMetadata = ():
	| {
			appPermissions: {
				hasSoftwareAccess: boolean;
				hasServiceDeskAccess: boolean;
				hasCoreAccess: boolean;
			};
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			licensedProducts: any;
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			appEditions: any;
	  }
	| {
			appPermissions?: undefined;
			licensedProducts?: undefined;
			appEditions?: undefined;
	  } => {
	try {
		const appPermissions = {
			hasSoftwareAccess: getMeta('ajs-has-jira-software') === 'true',
			hasServiceDeskAccess: getMeta('ajs-has-jira-service-desk') === 'true',
			hasCoreAccess: getMeta('ajs-has-jira-core') === 'true',
		};
		const licensedProducts = JSON.parse(getMeta('ajs-licensed-products') || '{}');
		const appEditions = JSON.parse(getMeta('ajs-product-editions') || '{}');
		return {
			appPermissions,
			licensedProducts,
			appEditions,
		};
	} catch (error) {
		return {};
	}
};
